/**
 * PhoneNumber Value Object
 * Ensures phone numbers are valid and provides consistent formatting
 */
export class PhoneNumber {
  private static readonly US_PHONE_VALIDATION_PATTERN = /^1?\d{10}$/;
  private static readonly DIGITS_ONLY_PATTERN = /\D/g;
  private static readonly US_COUNTRY_CODE = '1';
  private static readonly DOMESTIC_PHONE_LENGTH = 10;
  private static readonly INTERNATIONAL_PHONE_LENGTH = 11;

  private constructor(private readonly normalizedDigits: string) {}

  static fromPhoneNumber(rawPhoneNumber: string): PhoneNumber {
    const digitsOnly = this.extractDigitsOnly(rawPhoneNumber);

    if (!this.isValidUsPhoneNumber(digitsOnly)) {
      throw new Error(`Invalid US phone number format: ${rawPhoneNumber}`);
    }

    return new PhoneNumber(digitsOnly);
  }

  static isValidUsPhoneNumber(phoneNumber: string): boolean {
    const digitsOnly = this.extractDigitsOnly(phoneNumber);
    return PhoneNumber.US_PHONE_VALIDATION_PATTERN.test(digitsOnly);
  }

  getNormalizedDigits(): string {
    return this.normalizedDigits;
  }

  formatForDisplay(): string {
    if (this.isDomesticNumber()) {
      return this.formatDomesticNumber();
    }

    if (this.isInternationalWithUsCountryCode()) {
      return this.formatInternationalNumber();
    }

    return this.normalizedDigits;
  }

  formatForInternationalDisplay(): string {
    const digitsWithoutCountryCode = this.removeCountryCodeIfPresent();
    return `+1 (${digitsWithoutCountryCode.slice(0, 3)}) ${digitsWithoutCountryCode.slice(3, 6)}-${digitsWithoutCountryCode.slice(6)}`;
  }

  getAreaCode(): string {
    const digitsWithoutCountryCode = this.removeCountryCodeIfPresent();
    return digitsWithoutCountryCode.slice(0, 3);
  }

  isEqualTo(otherPhoneNumber: PhoneNumber): boolean {
    return this.normalizedDigits === otherPhoneNumber.normalizedDigits;
  }

  private static extractDigitsOnly(phoneNumber: string): string {
    return phoneNumber.replace(PhoneNumber.DIGITS_ONLY_PATTERN, '');
  }

  private isDomesticNumber(): boolean {
    return this.normalizedDigits.length === PhoneNumber.DOMESTIC_PHONE_LENGTH;
  }

  private isInternationalWithUsCountryCode(): boolean {
    return (
      this.normalizedDigits.length === PhoneNumber.INTERNATIONAL_PHONE_LENGTH &&
      this.normalizedDigits.startsWith(PhoneNumber.US_COUNTRY_CODE)
    );
  }

  private formatDomesticNumber(): string {
    const digits = this.normalizedDigits;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private formatInternationalNumber(): string {
    const digits = this.normalizedDigits;
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  private removeCountryCodeIfPresent(): string {
    if (this.isInternationalWithUsCountryCode()) {
      return this.normalizedDigits.slice(1);
    }
    return this.normalizedDigits;
  }
}
